<template>
    <app-layout title="Create Order">
        <form @submit.prevent="showConfirmModals">
            <page-header>
                <template #title>Create Order</template>

                <template #actions>
                    <jet-button v-show="form.isDirty" type="submit" class="leading-none" :disabled="form.processing" >
                        Save
                    </jet-button>

                    <jet-tertiary-button
                        v-if="form.isDirty"
                        :disabled="form.processing"
                        class="leading-none"
                        @click="showUnsavedChangesModal = true"
                    >
                        Cancel
                    </jet-tertiary-button>

                    <jet-tertiary-button
                        v-else
                        :disabled="form.processing"
                        :href="route('orders.index')"
                        class="leading-none"
                    >
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <customer-form
                :customers="customers"
                :categories="categories"
                :holidays="holidays"
                :errors="errors"
                :form="form"
                class="px-3 md:px-4 lg:px-8"
            />

            <div class="px-3 md:px-4 lg:px-8 py-4">
                <order-items-edit
                    :order="order"
                    :form="form"
                    :customers="customers"
                    :categories="categories"
                    :is_wholesale="true"
                    :orderMinimum="orderMinimum"
                    :deliveryFeeBelowMinimum="deliveryFeeBelowMinimum"
                    v-show="form.customer_id"
                />
            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(backUrl)"
        />

        <jet-confirmation-modal :show="showEmailModal" :closeable="false">
            <template #title>
                Send confirmation?
            </template>

            <template #content>
                Do you want to email an order confirmation to the client?
            </template>

            <template #footer>
                <jet-button class="mr-2" @click="handleConfirmEmail('yes')" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Yes
                </jet-button>

                <jet-tertiary-button @click="handleConfirmEmail('no')">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import CustomerForm from '@/Pages/Orders/Partials/CustomerForm.vue';
import OrderItemsEdit from '@/Shared/Orders/OrderItemsEdit';
import JetButton from '@/Jetstream/Button';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';
import { DateTime } from 'luxon';

export default {
    setup(props) {
        const form = useForm({
            id: null,
            order_date: new Date(),
            ship_date: null,
            payment_status: 'unpaid',
            shipping_method: 'delivery',
            override_ship_date: false,
            customer_id: null,
            delivery_fee: null,
            applied_credit_amount: null,
            override_stock_restrictions: false,
            order_items: [],
            notes: '',
            email_confirmation: false,
            is_pricing_locked: false,
            is_admin: props.is_admin,
        })

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        CustomerForm,
        OrderItemsEdit,
        JetButton,
        JetTertiaryButton,
        JetConfirmationModal,
        UnsavedChangesModal2,
    },

    props: ['order', 'customers', 'categories', 'holidays', 'errors', 'orderMinimum', 'deliveryFeeBelowMinimum', 'is_admin'],

    data() {
        return {
            formRoute: this.route('orders.store'),
            itemToAdd: null,
            showEmailModal: false,
            showUnsavedChangesModal: false,
        }
    },

    methods: {
        showConfirmModals () {
            this.showEmailModal = true;
        },

        handleConfirmEmail (response) {
            this.form.email_confirmation = response === 'yes' ? true : false;
            this.showEmailModal = false;
            this.handleSubmit();
        },

        handleSubmit () {
            if(!this.form.processing){
                this.form
                    .transform((data) => ({
                        ...data,
                        order_date: data.order_date ? this.formatDate(data.order_date) : null,
                        ship_date: data.ship_date ? this.formatDate(data.ship_date) : null,
                    }))
                    .post(route('orders.store'));
            }
        },

        formatDate (date) {
            return DateTime.fromJSDate(date).toSQLDate();
        },
    },
    computed: {
        backUrl () {
            const params = route().params;

            if (params.return) {
                return params.return;
            }

            return route('orders.index', {
                page: this.$page.props.page.orders,
            });
        },
    },
}
</script>

<style scoped>
.multi-custom {
    min-height: 1.5rem;
}
</style>
